import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'

import { faBan, faCheckCircle } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import PageContext from '@contexts/pageContext'

import useCurrentUser from '@hooks/useCurrentUser'
import useProviderPackage, { generateUrls } from '@hooks/useProviderPackage'
import useProviderPackages from '@hooks/useProviderPackages'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import ProviderPackageListItem from '../ProviderPackageListItem'

const buildPageFilterFields = (isProviderDirector: boolean) => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'providers',
      },
      hidden: isProviderDirector,
      key: 'provider_id',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'min_price',
      label: 'Filter by Min Price',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'max_price',
      label: 'Filter by Max Price',
    },
  ]
}

interface PageContentProps {
  pageFilters: {
    [key: string]: any,
  },
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  // @ts-expect-error TS2741
  const { hidden }: { hidden: string } = parsePermittedQueryParams(getQueryParamsFromHash(), ['hidden'])
  const isHidden = hidden === 'true'

  const providerPackagesPayload = useProviderPackages({
    filters: { ...pageFilters, hidden: isHidden },
    performHttpRequests: true,
    requestOptions: { include_provider_package_provider: true },
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredProviderPackages,
    filteredProviderPackagesCount,
    hasFilteredProviderPackages,
    loading,
  } = providerPackagesPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const currentUserPayload = useCurrentUser()
  const { isProviderDirector, provider } = currentUserPayload

  const { callbacks: { addProviderPackage } } = useProviderPackage({ provider_id: provider?.id })

  const { hiddenProviderPackagesIndexUrl, providerPackagesIndexUrl } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Packages | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => addProviderPackage()} size="medium">
            Create
          </Button>
        )}
        activeTabBarItemKey={isHidden ? 'archived' : 'active'}
        tabBarItems={[
          {
            href: providerPackagesIndexUrl,
            icon: faCheckCircle,
            key: 'active',
            title: 'Active',
          },
          {
            href: hiddenProviderPackagesIndexUrl,
            icon: faBan,
            key: 'archived',
            title: 'Archived',
          },
        ]}
        title="Packages"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredProviderPackages && <ListBlankState />}

            {!loading && hasFilteredProviderPackages && (
              <DashboardModule title="Packages">
                <Box flexDirection="column">
                  <Box flexDirection="column">
                    {filteredProviderPackages.map(providerPackage => (
                      // @ts-expect-error TS2322
                      <ProviderPackageListItem key={providerPackage.id} providerPackage={providerPackage} />
                    ))}
                  </Box>

                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredProviderPackagesCount}
                    loading={loading}
                  />
                </Box>
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields(isProviderDirector)}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

PageContent.propTypes = {
  pageFilters: PropTypes.object.isRequired,
}

export default PageContent
